import React from 'react';
import { useSelector } from 'react-redux';
import {
    Drawer,
    Tabs,
    Tab,
    IconButton,
    Chip,
    Stack,
    Box,
    Typography,
    useTokens,
    styled,
} from '@barracuda-internal/bds-core';
import {
    KeyboardArrowLeft,
    KeyboardArrowRight,
    Copy,
    Close,
} from '@barracuda-internal/bds-core/dist/Icons/Core';
import { useOpenScan } from './hooks/useOpenScan';
import { formatTimestamp } from '../../../common/utils/formatTimestamp';
import OverviewTab from './drawer/OverviewTab';
import FindingsTab from './drawer/FindingsTab';
import RawJsonTab from './drawer/RawJsonTab';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RESULT_TYPE = {
    allow:   'successNeutral',
    block:   'errorNeutral',
    partial: 'warningNeutral',
};

// ---------------------------------------------------------------------------
// Styled layout containers (3+ CSS properties → use styled with token props)
// ---------------------------------------------------------------------------

const DrawerHeader = styled(Stack, {
    shouldForwardProp: (p) => !['borderColor'].includes(p),
})(({ borderColor }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    borderBottom: `1px solid ${borderColor}`,
}));

const SummaryStrip = styled(Stack, {
    shouldForwardProp: (p) => !['borderColor'].includes(p),
})(({ borderColor }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    borderBottom: `1px solid ${borderColor}`,
}));

const TabBody = styled(Box)({
    flex: 1,
    overflowY: 'auto',
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ScanDetailDrawer() {
    const { openScanId, open, close } = useOpenScan();
    // Handle both test store shape (flat) and real app shape (guardian.scans)
    const scansState = useSelector((s) => s.guardian?.scans ?? s);
    const { current, list } = scansState;
    const [tab, setTab] = React.useState('overview');
    const tokens = useTokens();

    // Global Escape closes drawer
    React.useEffect(() => {
        function onKey(e) {
            if (e.key === 'Escape' && openScanId) close();
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [openScanId, close]);

    // Guard: nothing to render when drawer is closed or data hasn't arrived
    if (!openScanId) return null;
    if (!current) return null;

    const safeList = list || [];
    const idx = safeList.findIndex((r) => r.correlation_id === openScanId);
    const hasPrev = idx > 0;
    const hasNext = idx >= 0 && idx < safeList.length - 1;

    const ts = formatTimestamp(current.started_at);
    const findingsCount = (current.finding_categories || []).length;
    const latencyLabel =
        current.latency_ms != null
            ? current.latency_ms >= 1000
                ? `${(current.latency_ms / 1000).toFixed(1)}s`
                : `${current.latency_ms}ms`
            : '';

    return (
        <Drawer
            anchor="right"
            open
            onClose={close}
            PaperProps={{
                role: 'dialog',
                'aria-labelledby': 'scan-drawer-title',
                sx: {
                    width: { xs: '100vw', md: 480 },
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 320,
                    backgroundColor: tokens.aliasesColorBgPageDefault,
                    color: tokens.aliasesColorTextStaticEmphasis,
                },
            }}
        >
            {/* Header row — prev/next, correlation ID, copy, close */}
            <DrawerHeader
                borderColor={tokens.aliasesColorBorderStaticContainerSubtle}
                sx={{
                    padding: tokens.aliasesSpaceContainerDefault,
                    gap: tokens.aliasesSpaceComponentDefault,
                }}
            >
                <IconButton
                    aria-label="Previous scan"
                    disabled={!hasPrev}
                    onClick={() => open(safeList[idx - 1].correlation_id)}
                >
                    <KeyboardArrowLeft />
                </IconButton>
                <IconButton
                    aria-label="Next scan"
                    disabled={!hasNext}
                    onClick={() => open(safeList[idx + 1].correlation_id)}
                >
                    <KeyboardArrowRight />
                </IconButton>
                <Typography
                    id="scan-drawer-title"
                    variant="code"
                    sx={{
                        flex: 1,
                        color: tokens.aliasesColorTextStaticNeutral,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {current.correlation_id}
                </Typography>
                <IconButton
                    aria-label="Copy correlation ID"
                    onClick={() =>
                        navigator.clipboard.writeText(current.correlation_id)
                    }
                >
                    <Copy />
                </IconButton>
                <IconButton aria-label="Close" onClick={close}>
                    <Close />
                </IconButton>
            </DrawerHeader>

            {/* Summary strip — result, relative time, latency, content type */}
            <SummaryStrip
                borderColor={tokens.aliasesColorBorderStaticContainerSubtle}
                sx={{
                    padding: tokens.aliasesSpaceContainerDefault,
                    gap: tokens.aliasesSpaceComponentDefault,
                }}
            >
                {current.result_action && (
                    <Chip
                        bdsType={RESULT_TYPE[current.result_action] || 'staticNeutral'}
                        label={current.result_action}
                    />
                )}
                <Chip
                    bdsType="staticNeutral"
                    label={ts.relative}
                    title={ts.full}
                />
                {latencyLabel && (
                    <Chip bdsType="staticNeutral" label={latencyLabel} />
                )}
                {current.content_type && (
                    <Chip bdsType="staticNeutral" label={current.content_type} />
                )}
            </SummaryStrip>

            {/* Tab bar — hide MUI JS indicator; BDS Tab paints its own CSS underline on selection */}
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                TabIndicatorProps={{ style: { display: 'none' } }}
                sx={{ px: tokens.aliasesSpaceContainerDefault }}
            >
                <Tab value="overview" label="Overview" />
                <Tab value="findings" label={`Findings (${findingsCount})`} />
                <Tab value="raw" label="Raw JSON" />
            </Tabs>

            {/* Tab body */}
            <TabBody sx={{ padding: tokens.aliasesSpaceContainerDefault }}>
                {tab === 'overview' && <OverviewTab scan={current} />}
                {tab === 'findings' && <FindingsTab scan={current} />}
                {tab === 'raw' && <RawJsonTab scan={current} />}
            </TabBody>
        </Drawer>
    );
}
